import React, { useState } from 'react';
import { ModalWindow, TextField, ToggleSwitch } from 'ui-toolkit';
import { Formik } from 'formik';
import { useSelectCurrentOrder } from 'selectors/order/selectCurrentOrder';
import { IGiactContainerProps } from './GiactContainer';
import { BodyTextTW, BoldBodyTextTW } from 'tailwindComponents/bodyText/BodyTextTW';
import { HintTextTW } from 'tailwindComponents/formCellTW/HintTextTW';
import { ButtonTW } from 'tailwindComponents/buttonTW/ButtonTW';
import { useUpdateGiactMutation } from 'API/order/escrow/GiactAPI';
import { IGiactRequest } from 'types/order/escrow/request/IGiactRequest';
import { assertDefined } from 'utilities/Assertions';
import { checkForMutationSuccess } from 'API/checkForMutationSuccess';
import { eDisbursementType } from 'types/order/escrow/eDisbursementType';

interface IGiactModalProps extends IGiactContainerProps {
  onClose: () => void;
}

// we need this because Giact api requires ach_push instead of ach
const disbursementTypeMap = { [eDisbursementType.ACH]: 'ach_push', [eDisbursementType.Wire]: 'wire' };

export const GiactModal: React.FC<IGiactModalProps> = ({
  onClose,
  beneficiaryName,
  accountId,
  accountNumber,
  routingNumber,
  routingBankName,
  disbursementType,
  disbursementId,
}) => {
  const [checked, setChecked] = useState(false);
  const { data: order } = useSelectCurrentOrder();

  assertDefined(accountNumber);
  assertDefined(beneficiaryName);
  assertDefined(order.transaction.file_number);

  const [onUpdateGiact] = useUpdateGiactMutation();
  const beneficiaryNamePieces = beneficiaryName.split(' ');

  const firstName = beneficiaryNamePieces.shift() ?? '';
  const middleName = beneficiaryNamePieces.length >= 2 ? beneficiaryNamePieces.shift() : undefined;
  const lastName = beneficiaryNamePieces.join(' ');

  const onToggle = () => {
    setChecked(!checked);
  };

  const initialValues: IGiactRequest = {
    uniqueId: order.transaction.file_number,
    personEntity: {
      firstName,
      middleName,
      lastName,
    },
    bankAccountEntity: { accountNumber, routingNumber },
    accountId,
    businessEntity: { businessName: beneficiaryName },
    disbursementType: disbursementTypeMap[disbursementType],
    disbursementId,
  };

  const onSendGIACT = async (values: IGiactRequest) => {
    let request = { ...values };
    if (checked) {
      request.personEntity = undefined;
    } else {
      request.businessEntity = undefined;
    }

    if (values.accountId) {
      request.bankAccountEntity = undefined;
    }

    const result = await onUpdateGiact(request);
    if (checkForMutationSuccess(result, 'GIACT Request')) {
      onClose();
    }
  };

  return (
    <ModalWindow title={'GIACT Request'} minHeight={1} width={470} onClose={onClose}>
      <Formik initialValues={initialValues} onSubmit={() => {}}>
        {({ values }) => (
          <div className={'flex flex-col gap-4'}>
            {!checked && (
              <>
                <div className={'flex flex-row gap-2'}>
                  <TextField name={'personEntity.firstName'} label={'First Name'} className={'basis-1/2'} isRequired />
                  <TextField name={'personEntity.middleName'} label={'Middle Name'} className={'basis-1/2'} />
                </div>
                <TextField name={'personEntity.lastName'} label={'Last Name'} isRequired className={'basis-full'} />
              </>
            )}
            {checked && <TextField name={'businessEntity.businessName'} label={'Beneficiary Name'} isRequired />}
            <ToggleSwitch
              checked={checked}
              id={'giactToggle'}
              data-test-id={'giact-toggle'}
              onChange={() => onToggle()}
              value=""
              label={'Beneficiary is a business or entity'}
            />
            <div className={'flex flex-row text-left'}>
              <div className={'pr-6'}>
                <BoldBodyTextTW>ABA Routing Number</BoldBodyTextTW>
                <BodyTextTW>{routingNumber}</BodyTextTW>
                <HintTextTW hintText={routingBankName} className={'text-xs'} />
              </div>
              <div>
                <BoldBodyTextTW>Account Number</BoldBodyTextTW>
                <BodyTextTW>{accountNumber}</BodyTextTW>
              </div>
            </div>
            <div className={'flex justify-end gap-2'}>
              <ButtonTW title={'Cancel'} secondary onClick={onClose}>
                Cancel
              </ButtonTW>
              <ButtonTW title={'Send Request'} onClick={() => onSendGIACT(values)} data-test-id={'send-giact-button'}>
                Send Request
              </ButtonTW>
            </div>
          </div>
        )}
      </Formik>
    </ModalWindow>
  );
};
